/**
 * Shopper ACCOUNT-AREA guard -- closes GH #2941 defect 1.
 *
 * WHAT WAS BROKEN. Three shopper session routes carried NO guard of any kind
 * (`grep -c beforeEnter router/groups/storefront.js` returned 0):
 *
 *     /shop/:slug/profile          -> PersonalProfilePage
 *     /shop/:slug/addresses        -> StorefrontSavedAddresses
 *     /shop/:slug/payment-methods  -> StorefrontSavedPaymentMethods
 *
 * The comment above the profile route already stated the intended contract
 * verbatim -- "Logged-in shoppers only; guests reach their orders through the
 * tokenized link in the confirmation email, not through an account area." The
 * intent was documented; the enforcement was never written. Driven on a
 * sandbox: `/shop/acme/profile` with localStorage cleared and no login ever
 * performed rendered `Current: shopper1_acme@ui-test.example.com`, and served
 * change-password (with the current-password field), email-change and 2FA
 * enrolment controls beside it. Those are account-takeover primitives, not
 * merely PII.
 *
 * WHY `requireToken` DOES NOT ALREADY CATCH THIS. The storefront does not
 * leave an anonymous visitor logged out: it auto-provisions every one of them
 * into the store's SHARED shopper account via `POST /auth/getToken/<id>`
 * (api/src/endpoints/auth.py:901). That mints a REAL token on a REAL account
 * with no password, so an auth guard asking "is there a token?" is satisfied.
 * Every derivable signal -- isLoggedIn, isShopper, roleName, token, userId --
 * is IDENTICAL for an anonymous visitor and a person who signed in. The
 * distinction therefore cannot be derived; it has to be RECORDED at the one
 * moment it is knowable (the swap), which is what
 * `router/anonymousStorefrontSession.js` already does. Until now nothing
 * consumed that marker as a route guard. This module is that consumer.
 *
 * WHY A ROUTE GUARD RATHER THAN A MOUNTED COMPONENT. The same constraint that
 * forced `storefrontPasswordGate.js` to be a guard applies here, for the same
 * structural reason: these three routes are SIBLING route records, not
 * children of `StorefrontSessionGate`, so a component-mounted gate never
 * covered them -- that is exactly how the password wall was bypassed before.
 * A guard also resolves BEFORE the destination component is created, so the
 * account payload is never fetched and the identity never reaches the DOM,
 * not even briefly behind an overlay.
 *
 * WHY `beforeEnter` (per-route) RATHER THAN A GLOBAL `beforeEach`. The
 * password gate matches on a PATH PREFIX because its question -- "may you see
 * this store at all?" -- is true of the whole subtree, and a route added
 * tomorrow should inherit it. This question is the opposite shape: it is true
 * of exactly three routes and MUST NOT spread. `/shop/:slug/cart` and the
 * whole browse tree have to stay open to anonymous visitors -- that is the
 * core shopper journey, and `api/src/models/mixins/roles.py:451` deliberately
 * does not deny `Account` for precisely that reason (denying it breaks
 * anonymous add-to-cart). A path-prefix guard here would be one careless
 * pattern away from taking the cart with it. Naming the routes makes the
 * blast radius exactly as wide as the decision and no wider, and the
 * companion spec pins the set so a fourth account route cannot be added
 * without a deliberate edit here.
 *
 * WHAT THIS GUARD IS AND IS NOT. It is a UX/defence-in-depth boundary, not
 * the authorization boundary. Per Jake's scoping decision (2026-08-28) this
 * PR is the router guard ONLY; the API-layer redaction of the Account
 * serializer is a deliberate, tracked follow-up, because anonymous
 * add-to-cart depends on the same account row (its cart RPCs). A
 * determined visitor can still call the API directly. What this closes is the
 * far likelier path -- typing or following a URL -- and it stops the account
 * page from ever being constructed.
 *
 * FAIL-SAFE DIRECTION. `isAnonymousStorefrontSession()` reports ANONYMOUS
 * when storage cannot be read (see that module's header). This guard inherits
 * that direction deliberately: under uncertainty about whose session this is,
 * the safe answer to "should I show this person's saved payment methods?" is
 * no. The cost of being wrong that way is a redundant sign-in prompt; the
 * cost of the other way is the bug this closes.
 *
 * Behavioural tests: `__tests__/storefront-account-routes-guard.spec.js` (the
 * guard as a function) and `__tests__/storefront-account-routes-real-router.spec.js`
 * (real router; asserts the account page component is never CREATED).
 */

import { isAnonymousStorefrontSession } from "./anonymousStorefrontSession";

/**
 * Route name of the store HOME (`/shop/:slug`) -- where a visitor without a
 * personal session is turned (Jake, 2026-10-02).
 *
 * Not the store's `/signin`: that page is the store-PASSWORD form, not a
 * credential sign-in, and there is no storefront credential form for the
 * AdventHealth event (a real store login comes later). Not the generic
 * `/login` either: a shopper is never sent there. Plan:
 * docs/planning/2026-10-02-storefront-session-everywhere-plan.md
 *
 * Route `name:` values are deliberately stable across the URL migrations in
 * `groups/storefront.js`, so the name is a safer handle than the path.
 */
export const STORE_HOME_ROUTE = "storefront_home";

/**
 * The account surfaces this guard defends, BY ROUTE NAME.
 *
 * Named rather than pattern-matched so the set is a decision someone made,
 * reviewable in one place, and so adding a fourth account route is a
 * deliberate act rather than something that happens by accident of a path
 * shape. Pinned as a set by the companion spec.
 */
export const SHOPPER_ACCOUNT_ROUTE_NAMES = new Set([
    "storefront_profile",
    "storefront_saved_addresses",
    "storefront_saved_payment_methods",
]);

/**
 * Build the per-route `beforeEnter` guard.
 *
 * Returns `true` (allow) or a redirect to the store's own home.
 *
 * Deliberately NOT `false`. Returning false ABORTS the navigation and leaves
 * the visitor wherever they already were -- which, for someone arriving on a
 * fresh tab from a pasted link, is a blank app indistinguishable from a crash.
 * A visitor who is turned away must land somewhere they can act: the store's
 * home, where they can keep shopping (see STORE_HOME_ROUTE for why not a
 * sign-in page).
 *
 * The redirect carries the store `slug` so the shopper stays inside their
 * store's context and its branding. Bouncing to a global page would strip
 * both.
 */
/**
 * Does this browser hold ANY session at all? Since 2026-10-02 the account
 * pages are no longer behind `requireToken` (no storefront page is -- the
 * session gate owns storefront auth, and Jake's rule is that a shopper is
 * never sent to the generic /login). So a cold visitor with no session
 * reaches this guard too, and must be turned away as well: the anonymous
 * marker below cannot say so, because nothing has set it yet.
 */
function holdsNoSession() {
    try {
        return !localStorage.getItem("token");
    } catch {
        return true;
    }
}

export function createStorefrontAccountGuard() {
    return function storefrontAccountGuard(to) {
        if (!SHOPPER_ACCOUNT_ROUTE_NAMES.has(to?.name)) return true;
        if (!holdsNoSession() && !isAnonymousStorefrontSession()) return true;

        return {
            name: STORE_HOME_ROUTE,
            params: { slug: to?.params?.slug },
        };
    };
}

export default createStorefrontAccountGuard;
